import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// UnitContext — the multi-unit foundation's single source of truth for
// "which restaurant is the app currently scoped to."
//
// THE ZERO-BEHAVIOR-CHANGE CONTRACT
// ---------------------------------
// Existing single-unit accounts must behave EXACTLY as before. We
// guarantee that by keeping `currentRestaurantId` NULL for them. Every
// unit-aware data hook only adds `.eq('restaurant_id', id)` when this
// value is non-null, so a single-unit user's queries stay byte-identical
// and continue to rely on RLS membership scoping.
//
// MODES
//   'single'    — the user owns no restaurant_group that resolves to 2+
//                 restaurants. This is ALL existing users. The provider
//                 is inert: currentRestaurantId = null, isGrouped = false,
//                 no UnitSwitcher renders.
//   'group'     — an owned group has 2+ restaurants and a specific unit
//                 is selected. currentRestaurantId = that unit.
//   'all_units' — a grouped user selected the roll-up. currentRestaurantId
//                 = null (the GroupDashboard reads currentGroupId instead).
//
// SELECTION PRECEDENCE for the active unit (group mode):
//   1. localStorage['armCurrentUnitId'] IF it still exists in allUnits.
//   2. otherwise the first restaurant alphabetically by name.
// The resolved id is persisted back. A stored id that no longer maps to
// a live unit (unit removed, membership revoked) is dropped — the
// stale-localStorage guard.

const STORAGE_KEY = 'armCurrentUnitId'

const UnitContext = createContext(null)

export function useUnitContext() {
  const ctx = useContext(UnitContext)
  if (!ctx) {
    // Defensive: a consumer rendered outside the provider. Return an
    // inert single-unit shape so callers never crash and behave exactly
    // like a legacy single-unit account.
    return {
      mode: 'single',
      isGrouped: false,
      allUnits: [],
      currentRestaurantId: null,
      currentGroupId: null,
      switchUnit: () => {},
      switchToAllUnits: () => {},
      loading: false,
    }
  }
  return ctx
}

function readStoredUnitId() {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage.getItem(STORAGE_KEY)
  } catch (_e) {
    return null
  }
}

function writeStoredUnitId(id) {
  if (typeof window === 'undefined') return
  try {
    if (id) window.localStorage.setItem(STORAGE_KEY, id)
    else window.localStorage.removeItem(STORAGE_KEY)
  } catch (_e) {
    /* private mode / quota — non-fatal, selection just won't persist */
  }
}

export function UnitProvider({ children }) {
  // --- All hooks declared BEFORE any conditional return (React #310). ---
  const [allUnits, setAllUnits]               = useState([]) // [{id,name}]
  const [ownedGroups, setOwnedGroups]         = useState([]) // [{id,name}]
  const [selectedUnitId, setSelectedUnitId]   = useState(null)
  const [viewingAllUnits, setViewingAllUnits] = useState(false)
  const [loading, setLoading]                 = useState(true)

  // Load the user's restaurants + owned groups once the session is up.
  // RLS scopes both reads to the caller, so no explicit user filter is
  // needed. For a grouped owner, the additive restaurants_group_owner_select
  // policy makes every unit in their groups visible here.
  useEffect(() => {
    let mounted = true

    async function load() {
      try {
        const { data: sess } = await supabase.auth.getSession()
        if (!sess?.session) {
          // No session — stay inert. The auth gate in App handles UI.
          if (mounted) { setAllUnits([]); setOwnedGroups([]); setLoading(false) }
          return
        }

        const [restRes, groupRes] = await Promise.all([
          supabase
            .from('restaurants')
            .select('id, name, group_id, active, created_at')
            .eq('active', true)
            .order('name'),
          supabase
            .from('restaurant_groups')
            .select('id, name')
            .order('name'),
        ])

        if (!mounted) return

        // Group ownership is what unlocks multi-unit. Only restaurants
        // belonging to an OWNED group count toward "grouped" — a user
        // who happens to be a member of two unrelated restaurants is
        // still single-unit (no roll-up authority over them).
        const ownedGroupIds = new Set((groupRes.data ?? []).map((g) => g.id))
        const grouped = (restRes.data ?? []).filter(
          (r) => r.group_id && ownedGroupIds.has(r.group_id)
        )

        const units = grouped.map((r) => ({ id: r.id, name: r.name }))
        setAllUnits(units)
        setOwnedGroups(groupRes.data ?? [])

        // Resolve the active unit with the documented precedence, and
        // run the stale-localStorage guard against the fresh unit list.
        if (units.length >= 2) {
          const stored = readStoredUnitId()
          const storedIsLive = stored && units.some((u) => u.id === stored)
          if (!storedIsLive && stored) writeStoredUnitId(null) // drop stale id
          const resolved = storedIsLive ? stored : units[0].id // [0] = first alphabetically (ordered by name)
          setSelectedUnitId(resolved)
          writeStoredUnitId(resolved)
        } else {
          // Fewer than 2 owned-group units → single-unit. Clear any
          // leftover selection so the provider stays inert.
          setSelectedUnitId(null)
        }
      } catch (_e) {
        // Fail closed to single-unit so a transient error never strands
        // a legitimate single-unit user behind a broken switcher.
        if (mounted) { setAllUnits([]); setOwnedGroups([]); setSelectedUnitId(null) }
      } finally {
        if (mounted) setLoading(false)
      }
    }

    load()
    return () => { mounted = false }
  }, [])

  const isGrouped = allUnits.length >= 2

  const switchUnit = useCallback((restaurantId) => {
    setViewingAllUnits(false)
    setSelectedUnitId(restaurantId)
    writeStoredUnitId(restaurantId)
  }, [])

  const switchToAllUnits = useCallback(() => {
    setViewingAllUnits(true)
  }, [])

  const value = useMemo(() => {
    // currentGroupId — the owned group the selected unit belongs to (or
    // the sole owned group when viewing the roll-up). Foundation only
    // needs the first owned group; Ship 2 can refine for multi-group
    // owners.
    const currentGroupId = ownedGroups.length > 0 ? ownedGroups[0].id : null

    if (!isGrouped) {
      // SINGLE — inert. currentRestaurantId stays null so every hook's
      // query is byte-identical to the pre-multi-unit behavior.
      return {
        mode: 'single',
        isGrouped: false,
        allUnits: [],
        currentRestaurantId: null,
        currentGroupId: null,
        switchUnit,
        switchToAllUnits,
        loading,
      }
    }

    if (viewingAllUnits) {
      return {
        mode: 'all_units',
        isGrouped: true,
        allUnits,
        currentRestaurantId: null, // roll-up has no single unit scope
        currentGroupId,
        switchUnit,
        switchToAllUnits,
        loading,
      }
    }

    return {
      mode: 'group',
      isGrouped: true,
      allUnits,
      currentRestaurantId: selectedUnitId,
      currentGroupId,
      switchUnit,
      switchToAllUnits,
      loading,
    }
  }, [isGrouped, viewingAllUnits, allUnits, ownedGroups, selectedUnitId, loading, switchUnit, switchToAllUnits])

  return <UnitContext.Provider value={value}>{children}</UnitContext.Provider>
}
